import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { 
  Shield, CheckCircle, XCircle, AlertTriangle, 
  Clock, FileText, TrendingUp, Sparkles, ExternalLink
} from 'lucide-react';
import { shareAPI } from '../lib/api';

const SharedResults = () => {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchSharedAssessment();
  }, [token]);

  const fetchSharedAssessment = async () => {
    try {
      const response = await shareAPI.getSharedAssessment(token);
      setData(response.data);
    } catch (err) {
      if (err.response?.status === 404) {
        setError('This share link is invalid or has been revoked.');
      } else if (err.response?.status === 410) {
        setError('This share link has expired.');
      } else {
        setError('Unable to load assessment results.');
      }
    } finally {
      setLoading(false);
    }
  };

  const getTypeConfig = (type) => {
    const configs = {
      'CMMC': { bg: 'bg-blue-500/20', text: 'text-blue-400', border: 'border-blue-500/30' },
      'HIPAA': { bg: 'bg-green-500/20', text: 'text-green-400', border: 'border-green-500/30' },
      'NIST_CSF': { bg: 'bg-purple-500/20', text: 'text-purple-400', border: 'border-purple-500/30' }
    };
    return configs[type] || configs['HIPAA'];
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-violet-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-400">Loading assessment results...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-4">
          <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertTriangle className="w-8 h-8 text-red-400" />
          </div>
          <h1 className="text-2xl font-bold text-white mb-2">Link Unavailable</h1>
          <p className="text-slate-400 mb-6">{error}</p>
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 px-6 py-3 bg-violet-600 hover:bg-violet-700 text-white rounded-lg transition-colors"
          >
            <Sparkles className="w-4 h-4" />
            Go to Homepage
          </Link>
        </div>
      </div>
    );
  }

  const { assessment, summary, categoryBreakdown, deficiencies, deficiencyCount } = data;
  const typeConfig = getTypeConfig(assessment.assessmentType);
  const displayType = assessment.assessmentType === 'NIST_CSF' ? 'NIST CSF' : assessment.assessmentType;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      {/* Header */}
      <header className="border-b border-slate-700 bg-slate-900/50 backdrop-blur-lg sticky top-0 z-50">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-violet-500 to-purple-600 rounded-lg flex items-center justify-center">
                <Sparkles className="w-6 h-6 text-white" />
              </div>
              <div>
                <span className="text-violet-200 font-semibold">Aspiritech</span>
                <span className="text-slate-400 text-sm block">Assessment Results</span>
              </div>
            </div>
            <div className="text-sm text-slate-400">
              Shared Assessment Report
            </div>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-6 py-8">
        {/* Assessment Header */}
        <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700 mb-8">
          <div className="flex items-start justify-between">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <span className={`text-xs px-3 py-1 rounded-full ${typeConfig.bg} ${typeConfig.text}`}>
                  {displayType}
                </span>
                <span className={`text-xs px-3 py-1 rounded-full ${
                  assessment.status === 'Completed' 
                    ? 'bg-green-500/20 text-green-400' 
                    : 'bg-yellow-500/20 text-yellow-400'
                }`}>
                  {assessment.status}
                </span>
              </div>
              <h1 className="text-2xl font-bold text-white mb-2">{assessment.name}</h1>
              <div className="flex items-center gap-4 text-sm text-slate-400">
                <span className="flex items-center gap-1">
                  <Clock className="w-4 h-4" />
                  Last updated: {new Date(assessment.lastModified).toLocaleDateString()}
                </span>
              </div>
            </div>
            <div className="text-right">
              <div className="text-4xl font-bold text-white mb-1">
                {summary.complianceScore}%
              </div>
              <div className="text-sm text-slate-400">Compliance Score</div>
            </div>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
          <div className="bg-slate-800/50 rounded-xl p-4 border border-slate-700">
            <div className="flex items-center gap-2 mb-2">
              <FileText className="w-4 h-4 text-slate-400" />
              <span className="text-slate-400 text-sm">Total</span>
            </div>
            <div className="text-2xl font-bold text-white">{summary.totalQuestions}</div>
          </div>
          <div className="bg-slate-800/50 rounded-xl p-4 border border-slate-700">
            <div className="flex items-center gap-2 mb-2">
              <TrendingUp className="w-4 h-4 text-blue-400" />
              <span className="text-slate-400 text-sm">Answered</span>
            </div>
            <div className="text-2xl font-bold text-blue-400">{summary.answered}</div>
          </div>
          <div className="bg-slate-800/50 rounded-xl p-4 border border-slate-700">
            <div className="flex items-center gap-2 mb-2">
              <CheckCircle className="w-4 h-4 text-green-400" />
              <span className="text-slate-400 text-sm">Compliant</span>
            </div>
            <div className="text-2xl font-bold text-green-400">{summary.compliant}</div>
          </div>
          <div className="bg-slate-800/50 rounded-xl p-4 border border-slate-700">
            <div className="flex items-center gap-2 mb-2">
              <XCircle className="w-4 h-4 text-red-400" />
              <span className="text-slate-400 text-sm">Non-Compliant</span>
            </div>
            <div className="text-2xl font-bold text-red-400">{summary.nonCompliant}</div>
          </div>
          <div className="bg-slate-800/50 rounded-xl p-4 border border-slate-700">
            <div className="flex items-center gap-2 mb-2">
              <AlertTriangle className="w-4 h-4 text-slate-400" />
              <span className="text-slate-400 text-sm">N/A</span>
            </div>
            <div className="text-2xl font-bold text-slate-400">{summary.notApplicable}</div>
          </div>
        </div>

        {/* Category Breakdown */}
        <div className="bg-slate-800/50 rounded-xl border border-slate-700 mb-8 overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-700">
            <h2 className="text-lg font-semibold text-white">Category Breakdown</h2>
          </div>
          <div className="p-6">
            <div className="space-y-4">
              {categoryBreakdown.map((category, index) => {
                const percentage = category.complianceScore;
                return (
                  <div key={index}>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-white font-medium">{category.category}</span>
                      <span className={`text-sm font-semibold ${
                        percentage >= 80 ? 'text-green-400' :
                        percentage >= 50 ? 'text-yellow-400' : 'text-red-400'
                      }`}>
                        {percentage}%
                      </span>
                    </div>
                    <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
                      <div 
                        className={`h-full rounded-full transition-all ${
                          percentage >= 80 ? 'bg-green-500' :
                          percentage >= 50 ? 'bg-yellow-500' : 'bg-red-500'
                        }`}
                        style={{ width: `${percentage}%` }}
                      />
                    </div>
                    <div className="flex items-center gap-4 mt-1 text-xs text-slate-500">
                      <span>{category.totalQuestions} total</span>
                      <span className="text-green-400">{category.compliant} compliant</span>
                      <span className="text-red-400">{category.nonCompliant} non-compliant</span>
                      <span>{category.notApplicable} N/A</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Deficiencies */}
        {deficiencies.length > 0 && (
          <div className="bg-slate-800/50 rounded-xl border border-slate-700 overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-700 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-white">
                Top Findings ({deficiencyCount} total)
              </h2>
              <span className="text-xs px-2 py-1 rounded-full bg-red-500/20 text-red-400">
                Requires Attention
              </span>
            </div>
            <div className="divide-y divide-slate-700">
              {deficiencies.slice(0, 10).map((item, index) => (
                <div key={index} className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-8 h-8 bg-red-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                      <XCircle className="w-4 h-4 text-red-400" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-xs px-2 py-0.5 rounded bg-slate-700 text-slate-300">
                          {item.category}
                        </span>
                        {item.subCategory && (
                          <span className="text-xs text-slate-500">{item.subCategory}</span>
                        )}
                      </div>
                      <p className="text-white mb-3">{item.question}</p>
                      {item.remediation && (
                        <div className="bg-slate-700/50 rounded-lg p-4">
                          <h4 className="text-sm font-medium text-green-400 mb-2">Remediation Steps:</h4>
                          <p className="text-sm text-slate-300 whitespace-pre-line">{item.remediation}</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
            {deficiencyCount > 10 && (
              <div className="px-6 py-4 border-t border-slate-700 text-center">
                <p className="text-sm text-slate-400">
                  Showing 10 of {deficiencyCount} findings. Contact your assessor for the full report.
                </p>
              </div>
            )}
          </div>
        )}

        {/* Footer */}
        <div className="mt-12 text-center">
          <div className="inline-flex items-center gap-2 text-slate-500 text-sm">
            <Shield className="w-4 h-4" />
            <span>Powered by Aspiritech Cybersecurity Assessment Tool</span>
          </div>
          <p className="text-slate-600 text-xs mt-2">
            This is a shared view of assessment results. For full access, contact your organization.
          </p>
        </div>
      </main>
    </div>
  );
};

export default SharedResults;
